<template>
	<div
		class="group-settings-wrapper"
		:class="{
			'announcements-banner-group-settings-open':
				store.showAnnouncementsBanner || store.askForNotificationPermission,
			'announcements-banner-group-settings-close':
				!store.showAnnouncementsBanner && !store.askForNotificationPermission,
		}"
	>
		<div class="group-settings">
			<div class="group-settings-heading">
				<div class="go-back" @click="goBack">
					<img
						class="icon-button"
						src="/images/common/large-grey-close.png"
						alt="Back"
					/>
				</div>
				<h2 class="group-settings-header">
					{{
						this.isDM
							? $t('groupsettings_dm_title')
							: $t('groupsettings_bubblesettings_title')
					}}
				</h2>
			</div>

			<DMOptions v-if="isDM" :chatId="chat.id" />

			<Tabs :tabs="tabs" :defaultSelected="defaultSelectedTab.id">
				<template v-slot="{ tab }">
					<RouterLink :to="{ name: tab.id, params: $route.params }">
						{{ tab.text }}
					</RouterLink>
				</template>
			</Tabs>

			<div class="group-settings-content" :class="{ 'dm-settings': isDM }">
				<router-view v-if="!isLoading" />

				<div class="loading-spinner" v-if="isLoading">
					<IosSpinner />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import store, { getCurrentChat, getChat } from '@/store'
import { Tabs } from '@/components/common'
import IosSpinner from '@/components/IosSpinner'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import { addEventBusEvent } from '@/helpers/utils'
import { chatCan, ADD_MEMBER } from '@/helpers/permissions'
import DMOptions from './DMOptions'
import { fetchMyMembershipForChat } from '@/api/chat'

export default {
	components: {
		IosSpinner,
		Tabs,
		DMOptions,
	},
	props: ['bubbleId'],
	data() {
		return {
			store,
			isLoading: true,
			chat: null,
		}
	},

	computed: {
		isDM() {
			return this.chat?.isdm ?? false
		},
		isMultiDM() {
			return this.chat?.isMultiDM ?? false
		},
		tabs() {
			const canAdd = !this.isDM && chatCan(this.chat, ADD_MEMBER)
			return [
				{ id: 'groupoptions', text: this.$t('groupsettings_options_title') },
				!this.isDM && {
					id: 'grouppeople',
					text: this.$t('admin_groups_members_label'),
				},
				canAdd && {
					id: 'groupinvites',
					text: this.$t('groupinvites_title_invite'),
				},
				{
					id: 'groupnotifs',
					text: this.$t('groupsettings_notifications_title'),
				},
				this.isDM &&
					!this.isMultiDM && {
						id: 'groupcommon',
						text: this.$t('settings_common_groups_title'),
					},
				{ id: 'groupmedia', text: this.$t('groupsettings_media_title') },
			].filter((x) => x)
		},

		defaultSelectedTab() {
			return this.tabs.find((t) => t.id === this.$route.name) || {}
		},
	},

	async mounted() {
		if (!store.currentChatId) {
			store.currentChatId = this.bubbleId
		}
		await this.fetchGroup()
		this.chat = getCurrentChat()
		this.isLoading = false

		this.escapeId = addEscapeListener(() => {
			this.goBack()
		})

		this.removeEvent = addEventBusEvent(
			['chat-update', 'membership-update'],
			() => {
				this.chat = getChat(this.bubbleId)
			}
		)
	},

	beforeUnmount() {
		this.removeEvent()
		removeEscapeListener(this.escapeId)
	},

	methods: {
		goBack() {
			this.$router.push({ name: 'chat', params: this.$route.params })
		},

		async fetchGroup() {
			try {
				fetchMyMembershipForChat(this.bubbleId)
			} catch (e) {
				this.$alert.error(e)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

@media screen and (max-width: 1000px) {
	.group-settings-wrapper {
		left: 0px !important;
	}
}

.group-settings-heading {
	position: relative;
}

.go-back {
	position: absolute;
	left: 0;
	top: 10px;
	width: 20px;
	height: 20px;
	cursor: pointer;
}

:deep(.tabs-container) {
	margin: 45px 0 24px 0;
	.tab-item {
		text-transform: uppercase;
		font-family: Avenir-Black;
		font-size: 12px;
		color: $neutral-500;
		&.selected {
			border-color: var(--teal-300) !important;
		}
		&.selected > a,
		&.selected:hover > a {
			cursor: default;
			color: var(--teal-300);
		}
		&:hover > a {
			color: var(--neutral-600);
		}
		> a {
			color: var(--neutral-400);
			letter-spacing: 1.4px;
			text-decoration: none;
			padding: 0 24px 16px 24px;
		}
	}
}

.group-settings-header {
	grid-column-start: 2;
	padding-left: 20px;
	margin-bottom: 20px;
	font-family: Avenir-Heavy;
	font-size: 30px;
	font-weight: normal;
	line-height: 1.2;
	text-align: center;
	color: var(--neutral-800);
}

.group-settings {
	display: block;
	width: 830px;
	margin: 0 auto;
	padding: 0 20px;
}

.group-settings-content {
	height: calc(100vh - 224px);
	overflow-x: hidden;
	overflow-y: auto;

	&.dm-settings {
		height: calc(100vh - 376px);
	}
	.loading-spinner {
		padding: 60px 0;
		width: 100%;
		:deep(.spinner-div) {
			position: static !important;
		}
	}
}

.group-settings-wrapper {
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0;
	left: 280px;
	background-color: var(--neutral-0);
	z-index: 7;
	padding-bottom: 40px;
}

.announcements-banner-group-settings-open {
	transition-duration: 400ms;
	transition-delay: 1200ms;
	transition-timing-function: ease-in-out;
	transition-property: all;
}

.announcements-banner-group-settings-close {
	top: 0 !important;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.app-layout {
	&.announcement {
		.group-settings {
			.group-settings-content {
				height: calc(100vh - 224px - #{$announcement-banner-height});
			}
		}
	}
}
@include overrideScheme('dark') {
	.group-settings-wrapper {
		background-color: var(--neutral-100);
	}
}
</style>
